/**
 * Teams Notifications — Microsoft Teams incoming webhook configuration.
 *
 * Customer setup flow (shown in the UI) — updated for Teams Workflows app:
 *   Microsoft deprecated the old "Connectors → Incoming Webhook" flow (deadline April 30, 2026).
 *   New flow uses the Teams Workflows app:
 *   1. In Teams: open a channel → click Apps (or ⋯ → Workflows)
 *   2. Search "Post to a channel when a webhook request is received" → Add
 *   3. Copy the generated URL (starts with https://prod-XX.*.logic.azure.com/...)
 *   4. Paste it here, name it, pick trigger events, Save, then Test
 *
 *   Backend: accepts any https:// URL — webhook.office.com (legacy) and logic.azure.com (new) both work.
 *   Legacy webhook.office.com URLs will stop working after April 30, 2026.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import IntegrationActivityLog from "../IntegrationActivityLog";

interface TeamsConfig {
  id: number;
  name: string;
  webhookUrl: string;
  notifyOnResolve: boolean;
  notifyOnNewChat: boolean;
  notifyOnMissed: boolean;
  notifyOnSlaBreach: boolean;
  notifyOnBotHandoff: boolean;
  notifyOnNoAgentsOnline: boolean;
  notifyOnQueueDepth: boolean;
  queueDepthThreshold: number;
  teamId: number | null;
  isEnabled: boolean;
  createdAt: string;
}

interface FormState {
  name: string;
  webhookUrl: string;
  notifyOnResolve: boolean;
  notifyOnNewChat: boolean;
  notifyOnMissed: boolean;
  notifyOnSlaBreach: boolean;
  notifyOnBotHandoff: boolean;
  notifyOnNoAgentsOnline: boolean;
  notifyOnQueueDepth: boolean;
  queueDepthThreshold: number;
  teamId: number | null;
}

interface Team {
  id: number;
  name: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  webhookUrl: "",
  notifyOnResolve: true,
  notifyOnNewChat: false,
  notifyOnMissed: false,
  notifyOnSlaBreach: false,
  notifyOnBotHandoff: false,
  notifyOnNoAgentsOnline: false,
  notifyOnQueueDepth: false,
  queueDepthThreshold: 5,
  teamId: null
};

export default function TeamsNotifications() {
  const api = useApi();
  const { enableTeamsNotifications } = useAdminFeatures();
  const [configs, setConfigs] = useState<TeamsConfig[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<TeamsConfig | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [testing, setTesting] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, { success: boolean; message: string }>>({});
  const [deleting, setDeleting] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, teamRes] = await Promise.all([
        api.admin.get("TeamsNotification"),
        api.messaging.get("Teams/List")
      ]);
      const configData = await configRes.json();
      const teamData = await teamRes.json();
      setConfigs(Array.isArray(configData) ? configData : []);
      setTeams(Array.isArray(teamData) ? teamData : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSaveError("");
    setShowModal(true);
  }

  function openEdit(c: TeamsConfig) {
    setEditing(c);
    setForm({
      name: c.name,
      webhookUrl: c.webhookUrl,
      notifyOnResolve: c.notifyOnResolve,
      notifyOnNewChat: c.notifyOnNewChat,
      notifyOnMissed: c.notifyOnMissed,
      notifyOnSlaBreach: c.notifyOnSlaBreach,
      notifyOnBotHandoff: c.notifyOnBotHandoff ?? false,
      notifyOnNoAgentsOnline: c.notifyOnNoAgentsOnline ?? false,
      notifyOnQueueDepth: c.notifyOnQueueDepth ?? false,
      queueDepthThreshold: c.queueDepthThreshold ?? 5,
      teamId: c.teamId ?? null
    });
    setSaveError("");
    setShowModal(true);
  }

  async function save() {
    if (!form.webhookUrl.trim()) { setSaveError("Webhook URL is required."); return; }
    setSaving(true);
    setSaveError("");
    try {
      const body = {
        name: form.name || "Teams Channel",
        webhookUrl: form.webhookUrl,
        notifyOnResolve: form.notifyOnResolve,
        notifyOnNewChat: form.notifyOnNewChat,
        notifyOnMissed: form.notifyOnMissed,
        notifyOnSlaBreach: form.notifyOnSlaBreach,
        notifyOnBotHandoff: form.notifyOnBotHandoff,
        notifyOnNoAgentsOnline: form.notifyOnNoAgentsOnline,
        notifyOnQueueDepth: form.notifyOnQueueDepth,
        queueDepthThreshold: form.queueDepthThreshold || 5,
        teamId: form.teamId ?? null,
        isEnabled: editing?.isEnabled ?? true
      };
      const res = editing
        ? await api.admin.put(`TeamsNotification/${editing.id}`, body)
        : await api.admin.post("TeamsNotification", body);
      const data = await res.json();
      if (!res.ok) { setSaveError(data?.error ?? "Save failed."); return; }
      setShowModal(false);
      await load();
    } catch {
      setSaveError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(c: TeamsConfig) {
    try {
      await api.admin.put(`TeamsNotification/${c.id}`, {
        name: c.name,
        webhookUrl: c.webhookUrl,
        notifyOnResolve: c.notifyOnResolve,
        notifyOnNewChat: c.notifyOnNewChat,
        notifyOnMissed: c.notifyOnMissed,
        notifyOnSlaBreach: c.notifyOnSlaBreach,
        notifyOnBotHandoff: c.notifyOnBotHandoff ?? false,
        notifyOnNoAgentsOnline: c.notifyOnNoAgentsOnline ?? false,
        notifyOnQueueDepth: c.notifyOnQueueDepth ?? false,
        queueDepthThreshold: c.queueDepthThreshold ?? 5,
        teamId: c.teamId ?? null,
        isEnabled: !c.isEnabled
      });
      await load();
    } catch { /* ignore */ }
  }

  async function deleteConfig(id: number) {
    if (!confirm("Delete this Teams webhook configuration?")) return;
    setDeleting(id);
    try {
      await api.admin.delete(`TeamsNotification/${id}`);
      await load();
    } catch { /* ignore */ } finally {
      setDeleting(null);
    }
  }

  async function test(id: number) {
    setTesting(id);
    setTestResults(prev => ({ ...prev, [id]: undefined as any }));
    try {
      const res = await api.admin.post(`TeamsNotification/Test/${id}`, {});
      const data = await res.json();
      setTestResults(prev => ({ ...prev, [id]: data }));
    } catch {
      setTestResults(prev => ({ ...prev, [id]: { success: false, message: "Test request failed." } }));
    } finally {
      setTesting(null);
    }
  }

  const triggers = (c: TeamsConfig) => [
    c.notifyOnResolve        && "Resolved",
    c.notifyOnNewChat        && "New chat",
    c.notifyOnMissed         && "Missed",
    c.notifyOnSlaBreach      && "SLA breach",
    c.notifyOnBotHandoff     && "Bot handoff",
    c.notifyOnNoAgentsOnline && "No agents online",
    c.notifyOnQueueDepth     && `Queue ≥${c.queueDepthThreshold ?? 5}`
  ].filter(Boolean).join(", ") || "None";

  if (!enableTeamsNotifications) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <p className="text-sm font-semibold text-amber-800 mb-1">Teams Notifications — Upgrade Required</p>
          <p className="text-sm text-amber-700 mb-3">
            Microsoft Teams channel notifications are not included in your current plan.
            Contact support to enable this feature.
          </p>
          <Link to="/integrations/microsoft-365" className="text-sm text-blue-600 hover:underline">
            ← Back to Microsoft 365
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#6264A7]">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.5 3h-3.75V1.5a.75.75 0 0 0-1.5 0V3h-4.5V1.5a.75.75 0 0 0-1.5 0V3H4.5A1.5 1.5 0 0 0 3 4.5v15A1.5 1.5 0 0 0 4.5 21h15a1.5 1.5 0 0 0 1.5-1.5v-15A1.5 1.5 0 0 0 19.5 3Zm0 16.5h-15v-12h15v12Z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Teams Notifications</h1>
            <p className="text-sm text-slate-500">
              Send Adaptive Card alerts to Teams channels — resolve, missed, SLA breach, bot handoffs, queue alerts, and more.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/integrations/microsoft-365" className="text-sm text-blue-600 hover:underline">
            ← Microsoft 365
          </Link>
          <PrimaryBlueButton label="Add Webhook" onClick={openCreate} />
        </div>
      </div>

      {/* Setup instructions */}
      <SetupInstructions />

      {/* Webhook list */}
      {loading ? (
        <div className="text-sm text-slate-400 py-4">Loading...</div>
      ) : configs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <p className="text-sm font-medium text-slate-600 mb-1">No webhooks configured yet</p>
          <p className="text-xs text-slate-400 mb-4">Add a Teams incoming webhook to start receiving notifications.</p>
          <PrimaryBlueButton label="Add Your First Webhook" onClick={openCreate} />
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map(c => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex h-2 w-2 rounded-full flex-shrink-0 ${c.isEnabled ? "bg-green-500" : "bg-slate-300"}`} />
                    <span className="font-medium text-slate-800 text-sm">{c.name || "Unnamed"}</span>
                    {!c.isEnabled && (
                      <span className="text-xs text-slate-400 bg-slate-100 px-1.5 rounded">Disabled</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-mono mb-2">{c.webhookUrl}</p>
                  <p className="text-xs text-slate-500">
                    Triggers: <span className="text-slate-700">{triggers(c)}</span>
                  </p>
                  {c.teamId && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      Team: <span className="text-slate-600">{teams.find(t => t.id === c.teamId)?.name ?? `#${c.teamId}`}</span>
                    </p>
                  )}
                  {testResults[c.id] && (
                    <p className={`mt-2 text-xs ${testResults[c.id].success ? "text-green-700" : "text-red-600"}`}>
                      {testResults[c.id].success ? "✓ " : "✗ "}{testResults[c.id].message}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => test(c.id)}
                    disabled={testing === c.id}
                  >
                    {testing === c.id ? "Testing..." : "Test"}
                  </button>
                  <button
                    className="text-xs text-slate-500 hover:underline"
                    onClick={() => toggleEnabled(c)}
                  >
                    {c.isEnabled ? "Disable" : "Enable"}
                  </button>
                  <button
                    className="text-xs text-slate-500 hover:underline"
                    onClick={() => openEdit(c)}
                  >
                    Edit
                  </button>
                  <button
                    className="text-xs text-red-500 hover:underline"
                    onClick={() => deleteConfig(c.id)}
                    disabled={deleting === c.id}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="TeamsNotifications" />
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-5">
              {editing ? "Edit Webhook" : "Add Teams Webhook"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="e.g. Support Channel, Escalations"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Teams Incoming Webhook URL <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="https://prod-XX.westus.logic.azure.com/workflows/..."
                  value={form.webhookUrl}
                  onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))}
                />
                <p className="text-xs text-slate-400 mt-1">
                  In Teams: channel → ⋯ → Workflows → "Post to a channel when a webhook request is received" → Copy URL
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Team scope <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  value={form.teamId ?? ""}
                  onChange={e => setForm(f => ({ ...f, teamId: e.target.value ? parseInt(e.target.value) : null }))}
                >
                  <option value="">All teams (site-wide)</option>
                  {teams.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Restrict this webhook to a specific team — useful for routing queue-depth and no-agents alerts to the right Teams channel.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Notify on</label>
                <div className="space-y-2">
                  {[
                    { key: "notifyOnResolve",   label: "Conversation resolved",    desc: "When an agent marks a conversation resolved" },
                    { key: "notifyOnNewChat",   label: "New inbound conversation", desc: "When a new chat arrives in the queue" },
                    { key: "notifyOnMissed",    label: "Missed conversation",      desc: "When no agent accepts within the timeout" },
                    { key: "notifyOnSlaBreach", label: "SLA breach",               desc: "When a conversation exceeds its SLA threshold" }
                  ].map(t => (
                    <label key={t.key} className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={form[t.key as keyof FormState] as boolean}
                        onChange={e => setForm(f => ({ ...f, [t.key]: e.target.checked }))}
                      />
                      <div>
                        <p className="text-sm text-slate-700 group-hover:text-slate-900">{t.label}</p>
                        <p className="text-xs text-slate-400">{t.desc}</p>
                      </div>
                    </label>
                  ))}

                  {/* Pro+ events */}
                  <div className="pt-2 mt-1 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5">
                      Operational alerts
                      <span className="text-[10px] font-semibold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">Pro+</span>
                    </p>
                    {[
                      { key: "notifyOnBotHandoff",     label: "Bot escalated to live agent", desc: "When a workflow's transferToAgents node fires — AI couldn't handle it" },
                      { key: "notifyOnNoAgentsOnline", label: "No agents online",             desc: "A chat entered the queue but zero agents are available right now" }
                    ].map(t => (
                      <label key={t.key} className="flex items-start gap-3 cursor-pointer group mb-2">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={form[t.key as keyof FormState] as boolean}
                          onChange={e => setForm(f => ({ ...f, [t.key]: e.target.checked }))}
                        />
                        <div>
                          <p className="text-sm text-slate-700 group-hover:text-slate-900">{t.label}</p>
                          <p className="text-xs text-slate-400">{t.desc}</p>
                        </div>
                      </label>
                    ))}

                    {/* Queue depth with threshold input */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-2.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                        checked={form.notifyOnQueueDepth}
                        onChange={e => setForm(f => ({ ...f, notifyOnQueueDepth: e.target.checked }))}
                      />
                      <div className="flex-1">
                        <p className="text-sm text-slate-700">Queue depth threshold</p>
                        <p className="text-xs text-slate-400 mb-1.5">Alert when waiting conversations reach or exceed this count</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Alert when queue reaches</span>
                          <input
                            type="number"
                            min={1}
                            max={99}
                            className="w-14 border border-slate-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                            value={form.queueDepthThreshold}
                            onChange={e => setForm(f => ({ ...f, queueDepthThreshold: Math.max(1, parseInt(e.target.value) || 5) }))}
                            disabled={!form.notifyOnQueueDepth}
                          />
                          <span className="text-xs text-slate-500">or more</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {saveError && (
                <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{saveError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <SecondaryBlueButton label="Cancel" onClick={() => setShowModal(false)} disabled={saving} />
              <PrimaryBlueButton label={saving ? "Saving..." : "Save"} onClick={save} disabled={saving} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SetupInstructions() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-5 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>How to create a Teams Workflows webhook</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-slate-100 text-sm text-slate-600 space-y-3">
          <ol className="mt-4 space-y-3">
            {[
              { n: "1", text: "Open Microsoft Teams and navigate to the channel where you want notifications." },
              { n: "2", text: 'Click the ⋯ (More options) button next to the channel name → select Workflows.' },
              { n: "3", text: 'In the search box type "webhook" and select "Post to a channel when a webhook request is received".' },
              { n: "4", text: 'Click Add, give the flow a name (e.g. "Velaro Alerts"), then click Add again.' },
              { n: "5", text: 'Copy the webhook URL shown — it starts with https://prod-XX.*.logic.azure.com/...' },
              { n: "6", text: 'Paste it here, choose your triggers, Save, then click Test to verify.' }
            ].map(s => (
              <li key={s.n} className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-[#6264A7] text-white text-xs font-bold mt-0.5">{s.n}</span>
                <p className="text-xs text-slate-600 leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 mt-3">
            <strong>Using an older webhook.office.com URL?</strong> Microsoft retired the old Connectors flow on April 30, 2026.
            Existing webhook.office.com URLs no longer work — follow the steps above to create a new Workflows webhook URL and update it here.
          </div>
        </div>
      )}
    </div>
  );
}
